/**
 * Coordonnées d'une personne qu'une console crée (livreur, client SaaS, membre d'équipe) : e-mail OU numéro
 * WhatsApp, au moins un des deux. Fonctions PURES (sans React ni réseau), partagées par les consoles pour que la
 * règle, le corps envoyé aux edge functions et les messages d'erreur ne soient écrits qu'une fois.
 *
 * Contrat des edge functions (`create-driver-account`, `create-tenant`, `invite-member`) : `email?`, `phone?` en
 * E.164 (« +22370123456 »), `pays?` = code ISO ; avec un numéro seul il n'y a PAS de mot de passe temporaire.
 */
import { normaliserTelephone } from './api'
import { afficherTelephone } from '../../SwiftEats/supabase/functions/_shared/otp-whatsapp'

export { afficherTelephone }

export const FORMAT_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
export const MESSAGE_AU_MOINS_UN = 'Indiquez au moins un e-mail ou un numéro WhatsApp.'
export const MESSAGE_EMAIL_INVALIDE = 'Adresse e-mail invalide.'
export const MESSAGE_NUMERO_INVALIDE = "Ce numéro n'est pas valide."

/** Ce que saisit la personne dans un formulaire : l'e-mail tel quel, le numéro tel que le champ le rend. */
export interface SaisieContact {
  email: string
  telephone: { texte: string; iso: string }
}

export type ContactValide = { ok: true; email: string | null; phone: string | null; pays: string | null }
export type ContactRefuse = { ok: false; champ: 'email' | 'telephone' | 'aucun'; message: string }

/**
 * Règle « au moins un des deux ». Un champ REMPLI mais invalide est refusé même si l'autre est bon : on ne jette
 * pas en silence ce que la personne a tapé. `phone` sort en E.164, `pays` est le pays du numéro normalisé.
 */
export function validerContact(s: SaisieContact): ContactValide | ContactRefuse {
  const email = s.email.trim().toLowerCase()
  if (email && !FORMAT_EMAIL.test(email)) return { ok: false, champ: 'email', message: MESSAGE_EMAIL_INVALIDE }
  let phone: string | null = null
  let pays: string | null = null
  if (s.telephone.texte.trim()) {
    const r = normaliserTelephone(s.telephone.texte, s.telephone.iso)
    if (!r.ok) return { ok: false, champ: 'telephone', message: r.message }
    phone = r.e164
    pays = r.iso
  }
  if (!email && !phone) return { ok: false, champ: 'aucun', message: MESSAGE_AU_MOINS_UN }
  return { ok: true, email: email || null, phone, pays }
}

/**
 * Les clés `email`, `phone` et `pays` à fusionner dans le corps d'une edge function : seules les clés présentes
 * sont émises (une clé à `null` ou vide serait lue comme « fournie » par certaines fonctions).
 */
export function corpsContact(c: ContactValide): { email?: string; phone?: string; pays?: string } {
  return {
    ...(c.email ? { email: c.email } : {}),
    ...(c.phone ? { phone: c.phone, ...(c.pays ? { pays: c.pays } : {}) } : {}),
  }
}

/** Phrases françaises des nouveaux codes d'erreur des edge functions (le reste reste dans la table de chaque console). */
export const ERREURS_CONTACT: Record<string, string> = {
  invalid_phone: MESSAGE_NUMERO_INVALIDE,
  identifiants_differents:
    "Cet e-mail et ce numéro appartiennent à deux comptes différents. Utilisez les coordonnées d'une seule personne, ou laissez l'un des deux champs vide.",
  phone_already_used: 'Ce numéro est déjà utilisé par un autre compte.',
  comptes_multiples: 'Plusieurs comptes correspondent à cette adresse ou à ce numéro : contactez le support.',
}

/**
 * Phrase à afficher pour une erreur d'edge function. `table` = la table de la console (prioritaire, comme avant).
 * Exception : `invalid_phone` porte dans `detail` un message français déjà précis (« trop court », « pas valide pour
 * ce pays »…) qui prime sur la phrase générique.
 */
export function phraseErreurFonction(code: string, detail: string, table: Record<string, string>, repli = ''): string {
  if (code === 'invalid_phone') return detail || table['invalid_phone'] || MESSAGE_NUMERO_INVALIDE
  return table[code] || detail || code || repli
}

/** Réponse de `invite-member` (champs utiles à l'affichage). */
export interface ReponseInvitation {
  email?: string | null
  phone?: string | null
  connexion?: string | null
}

/**
 * Phrase de confirmation après une invitation. Numéro seul : aucun e-mail n'est parti, la personne se connecte avec
 * le code reçu sur WhatsApp et son invitation s'active à cette connexion. Compte déjà existant : accès ajouté.
 */
export function messageInvitation(r: ReponseInvitation): string {
  const numero = r.phone ? afficherTelephone(r.phone) : ''
  if (r.connexion === 'whatsapp' || (!r.email && numero)) {
    return `Invitation enregistrée : ${numero} se connecte avec son numéro WhatsApp (aucun e-mail envoyé).`
  }
  if (r.connexion === 'existant') {
    return `${r.email || numero} a déjà un compte : l'accès à l'organisation est ajouté.`
  }
  return `Invitation envoyée à ${r.email ?? numero}`
}

/** Ce qu'on affiche pour désigner une personne dont l'e-mail peut manquer : e-mail, sinon numéro formaté, sinon rien. */
export function contactAffiche(email: string | null | undefined, phone: string | null | undefined): string {
  if (email && email.trim()) return email.trim()
  return phone ? afficherTelephone(phone) : ''
}

/* ------------------------------------------------------------------ méthode de connexion mémorisée */

const CLE_METHODE = 'cw.methode'

/** Dernière méthode choisie sur cet appareil ; WhatsApp par défaut. Le stockage peut être bloqué : jamais d'exception. */
export function methodeMemorisee(): 'whatsapp' | 'email' {
  try {
    return window.localStorage.getItem(CLE_METHODE) === 'email' ? 'email' : 'whatsapp'
  } catch {
    return 'whatsapp'
  }
}

export function memoriserMethode(m: 'whatsapp' | 'email'): void {
  try {
    window.localStorage.setItem(CLE_METHODE, m)
  } catch { /* stockage bloqué : sans conséquence */ }
}
